import { getEventLocation } from './eventLocations';

const hhmm = (date) =>
  `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

const hasClockTime = (variant) => {
  if (!variant?.Data_inizio) return false;
  const start = new Date(variant.Data_inizio);
  return start.getHours() !== 0 || start.getMinutes() !== 0;
};

const intervalOf = (variant) => {
  if (!variant?.Data_inizio) return null;
  const start = new Date(variant.Data_inizio).getTime();
  if (Number.isNaN(start)) return null;
  const endRaw = variant.Data_Fine
    ? new Date(variant.Data_Fine).getTime()
    : start;
  const end = Number.isNaN(endRaw) ? start : Math.max(start, endRaw);
  return { start, end };
};

const schedulesOverlap = (a, b) => {
  const left = intervalOf(a);
  const right = intervalOf(b);
  if (!left || !right) return false;
  return left.start < right.end && right.start < left.end;
};

const overlapWindowLabel = (a, b) => {
  const left = intervalOf(a);
  const right = intervalOf(b);
  if (!left || !right) return null;
  if (!hasClockTime(a) && !hasClockTime(b)) return null;
  const start = new Date(Math.max(left.start, right.start));
  const end = new Date(Math.min(left.end, right.end));
  if (end.getTime() <= start.getTime()) return null;
  return `${hhmm(start)}–${hhmm(end)}`;
};

/**
 * Testo di avviso se tra le attività scelte ce ne sono almeno due con orari sovrapposti.
 * Gli estremi che si toccano (fine di una = inizio dell'altra) non contano.
 */
export const overlapWarning = (variants = []) => {
  const list = variants.filter(Boolean);
  const pairs = [];
  for (let i = 0; i < list.length; i += 1) {
    for (let j = i + 1; j < list.length; j += 1) {
      if (schedulesOverlap(list[i], list[j])) pairs.push([list[i], list[j]]);
    }
  }
  if (!pairs.length) return null;
  if (pairs.length === 1) {
    const [a, b] = pairs[0];
    const windowLabel = overlapWindowLabel(a, b);
    const when = windowLabel ? ` (${windowLabel})` : '';
    return `${a.Nome} e ${b.Nome} si sovrappongono${when}. Puoi iscriverti a entrambe: tieni conto degli orari.`;
  }
  const names = [...new Set(pairs.flat().map((variant) => variant.Nome))].join(
    ', '
  );
  return `${names} hanno orari che si sovrappongono. Puoi iscriverti a tutte: tieni conto degli orari.`;
};

/**
 * Scelta da applicare all'apertura del drawer, se il click arriva da una scheda.
 * Con acconto l'utente deve decidere tra acconto e totale.
 */
export const preselectChoice = ({
  variante,
  purchased,
  soldOut,
  hasAcconto,
  applyDiscount,
}) => {
  if (!variante?.documentId || purchased || soldOut || hasAcconto) return null;
  return {
    applyDiscount: Boolean(applyDiscount),
    variantId: variante.documentId,
    name: variante.Nome,
    priceOption: 'totale',
  };
};

/** 999 = capienza non definita: non mostriamo un numero. */
export const seatsLeftLabel = (availableSeats) => {
  if (availableSeats === 999 || availableSeats == null) return null;
  if (availableSeats <= 0) return 'Posti esauriti';
  if (availableSeats === 1) return '1 posto rimasto';
  return `${availableSeats} posti rimasti`;
};

const clockLabel = (start, end) => {
  const from = start ? String(start).slice(0, 5) : null;
  const to = end ? String(end).slice(0, 5) : null;
  if (from && to) return `${from}–${to}`;
  return from;
};

const dateLabel = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('it-IT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

export const eventRecap = (event) => {
  let when = null;
  if (event?.Recurring) {
    when = `Ogni ${event.Recurring}`;
  } else {
    const start = dateLabel(event?.Data);
    const end = dateLabel(event?.Date_end);
    const dates = start && end && end !== start ? `${start} – ${end}` : start;
    const hours = clockLabel(event?.Ora_inizio, event?.Ora_fine);
    when = [dates, hours].filter(Boolean).join(' · ') || null;
  }

  const location = getEventLocation(event?.Location);
  const city = location?.address
    ? location.address.split(',').slice(-1)[0].trim()
    : null;
  const where = location
    ? [location.title, city].filter(Boolean).join(' · ')
    : null;

  const audience =
    event?.Partecipanti ||
    (event?.tipoIscrizione === 'solo_soci'
      ? 'Riservato ai soci'
      : event?.tipoIscrizione === 'aperto_tutti'
        ? 'Aperto a tutti'
        : null);

  const description =
    typeof event?.Descrizione === 'string' ? event.Descrizione.trim() : '';

  return { when, where, audience, description };
};
